import { useEffect, useMemo, useState } from 'react'
import { Text, Textarea } from '@mantine/core'
import { getTaskArgsEntry } from './args-registry'

export interface ArgsEditorState {
  value: unknown
  error: string | null
}

interface TaskArgsEditorProps {
  slug: string
  /** Label for the JSON-textarea fallback. */
  label?: string
  /** Seed value (e.g. the task's scheduled_args); edits are owned by this component. */
  initialValue: unknown
  /** Reports the current { value, error } — fired on mount and after every edit. */
  onChange: (state: ArgsEditorState) => void
}

/**
 * Shared args editor (Run + Edit dialogs): the task's registered args component, or a raw
 * JSON textarea fallback, live-validated. The parent just consumes ArgsEditorState and
 * gates its submit button on `error`.
 */
export function TaskArgsEditor({
  slug,
  label = 'Args (JSON)',
  initialValue,
  onChange,
}: TaskArgsEditorProps) {
  const entry = getTaskArgsEntry(slug)

  // Custom-component path: structured value; the entry's validate() judges it.
  const [customValue, setCustomValue] = useState<unknown>(() => initialValue ?? undefined)
  // JSON fallback path: raw text; empty = no args.
  const [rawText, setRawText] = useState<string>(() =>
    initialValue == null ? '' : JSON.stringify(initialValue, null, 2),
  )

  const parsedJson = useMemo((): ArgsEditorState => {
    if (rawText.trim() === '') return { value: undefined, error: null }
    try {
      return { value: JSON.parse(rawText), error: null }
    } catch (err) {
      return { value: undefined, error: err instanceof Error ? err.message : 'Invalid JSON' }
    }
  }, [rawText])

  const value = entry ? customValue : parsedJson.value
  const error = entry ? entry.validate(customValue) : parsedJson.error

  useEffect(() => {
    onChange({ value, error })
  }, [value, error, onChange])

  if (entry) {
    return (
      <div>
        <entry.Component value={customValue} onChange={setCustomValue} />
        {error && (
          <Text size="sm" c="red" mt={4}>
            {error}
          </Text>
        )}
      </div>
    )
  }
  return (
    <Textarea
      label={label}
      description="Leave empty to omit args"
      autosize
      minRows={6}
      maxRows={16}
      classNames={{ input: 'font-mono text-xs' }}
      value={rawText}
      onChange={(event) => setRawText(event.currentTarget.value)}
      error={parsedJson.error}
    />
  )
}
